'use client';

import type { FC } from 'react';
import type { Swiper as SwiperCore } from 'swiper';
import type { SwiperOptions } from 'swiper/types';
import type { PromotionTallBannerItemType, PromotionTallBannerWidgetLogType } from '@weberus/shared-types';
import type { HomeLogPageNameValue } from '@/app/(pages)/sub-home/[category]/constants';
import { useEffect, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { YaTrackerImpression, clientLogStandardized } from '@weberus/client-tracker';

import { debounce } from 'lodash-es';
import { waitUntilClickTimeout, PC_BREAKPOINT } from '@weberus/utils';
import { PromotionTallBannerItem } from '@/app/_components/widgets/promotion-tall-banner/promotion-tall-banner-item';
import { navigateTo } from '@/app/_shared/utils/action-navigator';
import { SwiperNavButton } from '@/app/_components/common';

export interface SwiperProps {
  /** 로그 전송을 위한 data */
  pageNameLog: HomeLogPageNameValue;
  widgetLogMeta: PromotionTallBannerWidgetLogType;
  /** swiper 에 노출될 UI 아이템 */
  items: PromotionTallBannerItemType[];
  /** swiper 에서 선택된 index */
  selectedIndex: number;
  /** swiper 에서 선택된 index를 알려주기 위한 콜백함수 */
  setSelectedIndex: (index: number) => void;
  /** 외부에서 컨트롤 하기위한 Ref 객체*/
  swiperRef: React.MutableRefObject<SwiperCore | null>;
  /** 위젯 내 탭 영역이 존재하는 경우에 필요 */
  onChangeTab?: (index: number) => void;
  /** slide 좌우 버튼 노출 여부 */
  showNavButton?: boolean;
}

export const SwiperLayer: FC<SwiperProps> = ({
  pageNameLog,
  widgetLogMeta,
  items,
  setSelectedIndex,
  swiperRef,
  showNavButton,
}) => {
  const [windowWidth, setWindowWidth] = useState<number>(typeof window !== 'undefined' ? window.innerWidth : 0);
  useEffect(() => {
    const handleResize = debounce(() => {
      setWindowWidth(window.innerWidth);
    }, 200);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // swiper 기본 동작을 위한 설정
  const swiperSetting: SwiperOptions = {
    initialSlide: 0,
    loop: true,
    spaceBetween: windowWidth < PC_BREAKPOINT ? 12 : 20,
    centeredSlides: windowWidth < PC_BREAKPOINT,
    scrollbar: { draggable: true, el: null },
    slidesPerView: windowWidth < PC_BREAKPOINT ? 1.5 : 4,
    slidesPerGroup: windowWidth < PC_BREAKPOINT ? 1 : 4,
    breakpoints: {
      [PC_BREAKPOINT]: {
        centeredSlides: false,
        slidesPerView: 4,
        slidesPerGroup: 4,
      },
    },
  };

  const handleClientLog = (eventType: 'click' | 'impression', index: number, item: PromotionTallBannerItemType) => {
    const widgetPagePosition = windowWidth < PC_BREAKPOINT ? index + 1 : Math.floor(index / 4) + 1;
    const logData = { widgetPagePosition, ...item.logMeta, ...widgetLogMeta };
    const logWithEventData = eventType === 'impression' ? logData : { ...logData, deepLinkUri: item.action.app };

    return clientLogStandardized(eventType, pageNameLog, logWithEventData);
  };

  return (
    <div className="relative w-full pc:w-pc-size">
      <Swiper
        {...swiperSetting}
        scrollbar={{ draggable: true }}
        onSlideChange={(e) => {
          if (e.realIndex < items.length) {
            setSelectedIndex(e.realIndex);
          }
        }}
        onSwiper={(swiper) => {
          swiperRef.current = swiper; // Swiper 인스턴스 저장
        }}>
        {items.map((item, idx) => {
          return (
            <SwiperSlide
              key={idx}
              className="flex w-[calc(100%-7.2rem)] max-w-[calc(100%-7.2rem)] items-center pc:max-w-none">
              <YaTrackerImpression key={idx} onImpressed={() => handleClientLog('impression', idx, item)}>
                <PromotionTallBannerItem
                  {...item}
                  onClick={async (e) => {
                    e.preventDefault();
                    await waitUntilClickTimeout(handleClientLog('click', idx, item));
                    navigateTo(item.action);
                  }}
                />
              </YaTrackerImpression>
            </SwiperSlide>
          );
        })}
      </Swiper>
      {!!showNavButton && <SwiperNavButton swiperRef={swiperRef} />}
    </div>
  );
};
